/**
 * PATH: apps/ai/src/pages/dashboard/use-chat-engine.ts
 * PURPOSE: Chat state management — conversations, streaming, persistence, feedback
 * WHY: Separates all data/state logic from UI rendering for DashboardPage
 */

import { useState, useRef, useEffect, useCallback, type FormEvent, type KeyboardEvent } from 'react';
import { aiOrchestrator } from '../../services/aiAgents';
import { conversationService } from '../../services/conversationService';
import { useLocalStorage } from './utils';
import type { Message, Conversation, Citation } from './types';

export function useChatEngine() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [chainOfThought, setChainOfThought] = useState('');
  const [citations, setCitations] = useState<Citation[]>([]);
  const [currentModel, setCurrentModel] = useState<string>('');
  const [latencyMs, setLatencyMs] = useState<number>(0);
  const [showHistory, setShowHistory] = useState(false);
  const [skipCache, setSkipCache] = useState(false);
  const [conversationPrivacy, setConversationPrivacy] = useLocalStorage<'private' | 'shared'>('ai-hub-default-privacy', 'private');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState<string | null>(null);
  const [leftCollapsed, setLeftCollapsed] = useLocalStorage('ai-hub-left-collapsed', false);
  const [rightCollapsed, setRightCollapsed] = useLocalStorage('ai-hub-right-collapsed', true);
  const [backendConvMap, setBackendConvMap] = useState<Map<string, string>>(new Map());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const activeConversation = conversations.find(c => c.id === activeConversationId);
  const messages = activeConversation?.messages || [];

  // Auto-expand right panel when we get data
  useEffect(() => { if (citations.length > 0 || chainOfThought) setRightCollapsed(false); }, [citations, chainOfThought, setRightCollapsed]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);

  // Load conversations from backend on mount
  useEffect(() => {
    const loadConversations = async () => {
      try {
        const backendConvs = await conversationService.list({ include_shared: true, limit: 50 });
        const localConvs: Conversation[] = backendConvs.map(bc => ({
          id: bc.id, title: bc.title,
          messages: (bc.messages || []).map(bm => ({
            id: bm.id, role: bm.role as 'user' | 'assistant', content: bm.content, timestamp: new Date(bm.created_at),
            chainOfThought: bm.thinking || undefined,
            citations: bm.citations?.map(c => ({ sourceName: c.title || c.source_type, sourceType: c.source_type, relevance: c.relevance_score || 0, snippet: c.snippet, sourceUrl: c.source_url, sourceId: c.source_id, jobId: c.job_id, category: c.category })),
            model: bm.model_used || undefined, latencyMs: bm.latency_ms || undefined,
          })),
          createdAt: new Date(bc.created_at), updatedAt: new Date(bc.updated_at), privacy: bc.privacy,
        }));
        const newMap = new Map<string, string>();
        backendConvs.forEach(bc => newMap.set(bc.id, bc.id));
        setConversations(localConvs);
        setBackendConvMap(newMap);
        if (localConvs.length > 0 && !activeConversationId) setActiveConversationId(localConvs[0].id);
      } catch (err) {
        console.error('[AI Hub] Failed to load conversations:', err);
      }
    };
    loadConversations();
  }, []);

  const persistConversation = useCallback(async (localId: string, title: string) => {
    if (backendConvMap.has(localId)) return backendConvMap.get(localId)!;
    try {
      const bc = await conversationService.create({ title, privacy: conversationPrivacy });
      setBackendConvMap(prev => new Map(prev).set(localId, bc.id));
      setConversations(prev => prev.map(c => c.id === localId ? { ...c, id: bc.id } : c));
      setActiveConversationId(bc.id);
      return bc.id;
    } catch (err) {
      console.error('[AI Hub] Failed to persist conversation:', err);
      return localId;
    }
  }, [backendConvMap, conversationPrivacy]);

  const persistMessage = useCallback(async (convId: string, message: Message, cits?: Citation[], cot?: string): Promise<string | null> => {
    const backendId = backendConvMap.get(convId) || convId;
    try {
      const result = await conversationService.addMessage(backendId, {
        role: message.role, content: message.content, model_used: message.model, latency_ms: message.latencyMs, thinking: cot,
        citations: cits?.map(c => ({ source_type: c.sourceType as string, title: c.sourceName, snippet: c.snippet || '', relevance_score: c.relevance })),
      });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, messages: c.messages.map(m => m.id === message.id ? { ...m, backendId: result.id } : m) } : c));
      return result.id;
    } catch (err) {
      console.error('[AI Hub] Failed to persist message:', err);
      return null;
    }
  }, [backendConvMap]);

  const createConversation = (initialMessage?: string): string => {
    const id = `conv-${Date.now()}`;
    const now = new Date();
    setConversations(prev => [{ id, title: initialMessage?.slice(0, 50) || 'New conversation', messages: [], createdAt: now, updatedAt: now, privacy: 'private' }, ...prev]);
    setActiveConversationId(id);
    return id;
  };

  const deleteConversation = async (id: string) => {
    setConversations(prev => prev.filter(c => c.id !== id));
    if (activeConversationId === id) setActiveConversationId(null);
    const backendId = backendConvMap.get(id) || id;
    try { await conversationService.delete(backendId); setBackendConvMap(prev => { const next = new Map(prev); next.delete(id); return next; }); } catch { /* non-blocking */ }
  };

  const handleCopy = (content: string, id: string) => { navigator.clipboard.writeText(content); setCopied(id); setTimeout(() => setCopied(null), 2000); };

  const handleFeedback = async (messageId: string, rating: 1 | -1) => {
    if (!activeConversationId) return;
    const backendConvId = backendConvMap.get(activeConversationId);
    if (!backendConvId) return;
    const conv = conversations.find(c => c.id === activeConversationId);
    const msg = conv?.messages.find(m => m.id === messageId);
    if (!msg?.backendId) return;
    setFeedbackSubmitting(messageId);
    try {
      await conversationService.addFeedback(backendConvId, msg.backendId, { rating });
      setConversations(prev => prev.map(c => c.id === activeConversationId ? { ...c, messages: c.messages.map(m => m.id === messageId ? { ...m, feedbackRating: rating } : m) } : c));
    } catch (error) { console.error('Failed to submit feedback:', error); } finally { setFeedbackSubmitting(null); }
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;
    let convId = activeConversationId;
    let isNewConv = false;
    if (!convId) { convId = createConversation(content); isNewConv = true; }

    const userMessage: Message = { id: `msg-${Date.now()}`, role: 'user', content: content.trim(), timestamp: new Date() };

    (async () => { if (isNewConv) { convId = await persistConversation(convId!, content.slice(0, 50)); } persistMessage(convId!, userMessage); })();

    setConversations(prev => prev.map(c => c.id === convId ? { ...c, title: c.messages.length === 0 ? content.slice(0, 50) : c.title, messages: [...c.messages, userMessage], updatedAt: new Date() } : c));
    setInput(''); setIsLoading(true); setChainOfThought(''); setCitations([]); setCurrentModel(''); setLatencyMs(0);

    const assistantId = `msg-${Date.now() + 1}`;
    const startTime = Date.now();
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, messages: [...c.messages, { id: assistantId, role: 'assistant', content: '', timestamp: new Date() }] } : c));

    try {
      let accContent = '', accCoT = '';
      for await (const event of aiOrchestrator.processQuery(content, convId || 'default', { skipCache })) {
        if (event.type === 'thinking') { accCoT += `🔍 ${event.content}\n`; setChainOfThought(accCoT); }
        else if (event.type === 'meta') { setCurrentModel(event.model || 'QuotechAI'); if (event.intent) { accCoT += `📊 Intent: ${event.intent.type} | Domain: ${event.intent.domain}\n`; setChainOfThought(accCoT); } }
        else if (event.type === 'cot') { accCoT += `💭 ${event.content}\n`; setChainOfThought(accCoT); }
        else if (event.type === 'content') { accContent += event.content || ''; setConversations(prev => prev.map(c => c.id === convId ? { ...c, messages: c.messages.map(m => m.id === assistantId ? { ...m, content: accContent } : m) } : c)); }
        else if (event.type === 'done') {
          setLatencyMs(event.latencyMs || (Date.now() - startTime));
          if (event.model) setCurrentModel(event.model);
          if (event.citations?.length) setCitations(event.citations.map(c => ({ sourceName: c.sourceName, sourceType: c.sourceType, relevance: c.relevance, snippet: c.snippet, sourceUrl: c.sourceUrl, sourceId: c.sourceId, jobId: c.jobId, category: c.category })));
          if (event.suggestedRefinement) { accCoT += `\n💡 Suggestion: ${event.suggestedRefinement}`; setChainOfThought(accCoT); }
        } else if (event.type === 'error') { throw new Error(event.error || 'Unknown error'); }
      }
      if (!accContent) { accContent = 'I processed your request but received no content. Please try again.'; setConversations(prev => prev.map(c => c.id === convId ? { ...c, messages: c.messages.map(m => m.id === assistantId ? { ...m, content: accContent } : m) } : c)); }
      persistMessage(convId!, { id: assistantId, role: 'assistant', content: accContent, timestamp: new Date(), model: currentModel || 'QuotechAI', latencyMs: Date.now() - startTime }, citations, accCoT);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to get AI response';
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, messages: c.messages.map(m => m.id === assistantId ? { ...m, content: `⚠️ Error: ${msg}` } : m) } : c));
    } finally { setIsLoading(false); }
  };

  const addLocalMessage = (content: string, role: 'user' | 'assistant' = 'assistant') => {
    let convId = activeConversationId;
    if (!convId) { convId = createConversation('File Upload'); }
    const msg: Message = { id: `msg-${Date.now()}`, role, content, timestamp: new Date() };
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, messages: [...c.messages, msg], updatedAt: new Date() } : c));
    persistMessage(convId, msg);
  };

  const handleSubmit = (e: FormEvent) => { e.preventDefault(); sendMessage(input); };
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } };
  const startNewChat = () => { setActiveConversationId(null); setInput(''); setChainOfThought(''); setCitations([]); };

  return {
    conversations, activeConversationId, setActiveConversationId,
    activeConversation, messages, input, setInput,
    isLoading, copied, chainOfThought, citations, currentModel, latencyMs,
    showHistory, setShowHistory, skipCache, setSkipCache,
    conversationPrivacy, setConversationPrivacy,
    feedbackSubmitting, leftCollapsed, setLeftCollapsed, rightCollapsed, setRightCollapsed,
    backendConvMap, messagesEndRef, inputRef,
    sendMessage, addLocalMessage, handleSubmit, handleKeyDown, handleCopy, handleFeedback,
    deleteConversation, startNewChat,
  };
}
